import React, { useState } from "react";
import { Box, Tab, Tabs, Button, Typography, useMediaQuery, useTheme,Divider,styled } from "@mui/material";
import { Link, useNavigate } from "react-router-dom"; // Import useNavigate from react-router-dom
import logo from "../../assets/images/logo.png";
import FinanceImage from "../../assets/images/finance.jpg";
import BankImage from "../../assets/images/bank.jpg";
import HealthCareImage from "../../assets/images/healthcare.jpg";
import ItImage from "../../assets/images/it.jpg";
import LogisticsImage from "../../assets/images/logistics.jpg";
import { motion } from 'framer-motion';
import responsiveStyle from "./InputForm.css";
import { primaryColorPurple } from "../../Util/UtilColorPalete";

// Tab data with links
const tabData = [
  // { title: "Finance", image: FinanceImage, previewlink: 'https://finance.bigfivecrm.com/', formlink: 'https://templates.bigfivecrm.com/finance-datas/' },
  { title: "Finance", image: FinanceImage, previewlink: 'https://finance.bigfivecrm.com/', formlink: 'https://templates.bigfivecrm.com/finance-datas/' },
  { title: "Bank", image: BankImage, previewlink: 'https://bank.bigfivecrm.com/', formlink: 'https://templates.bigfivecrm.com/bank-form-page/' },
  { title: "Logistics", image: LogisticsImage, previewlink: 'https://logistics.bigfivecrm.com/', formlink: 'https://templates.bigfivecrm.com/logistics-form-page/' },
  { title: "IT", image: ItImage, previewlink: 'https://itservices.bigfivecrm.com/', formlink: 'https://templates.bigfivecrm.com/itservices-form-page/' },
  { title: "Healthcare", image: HealthCareImage, previewlink: 'https://healthcare.bigfivecrm.com/', formlink: 'https://templates.bigfivecrm.com/healthcare-form-page/' },
];

const textVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};
// Group images by category
const groupedData = tabData.reduce((acc, item) => {
  acc[item.title] = acc[item.title] || [];
  acc[item.title].push(item);
  return acc;
}, {});

const tabs = Object.keys(groupedData);

// Styled Components
const Card = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 12,
  overflow: 'hidden',
  boxShadow: theme.shadows[2],
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'scale(1.02)',
  },
}));

const ImageWrapper = styled(Box)({
  position: 'relative',
  overflow: 'hidden',
  height: 280,
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
});
// Custom styled Tab component
const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  minWidth: 'unset',
  padding: theme.spacing(1, 2),
  transition: theme.transitions.create(['background-color', 'color'], {
    duration: theme.transitions.duration.short,
  }),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
  },
}));
const ButtonGroup = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  background: theme.palette.background.paper,
}));
// Constants
const COLORS = {
  primary: '#6B5B95',
  hover: '#5A4A7D',
}
const TabPanel = React.memo(({ value, index, images }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleNavigate = (title) => {
    navigate(`/${title}`);
  };

  if (value !== index) {
    return null;
  }

  const ImageWrapper = styled('div')({
    overflow: 'hidden',
    position: 'relative',
    height: '300px', // Adjust this height as needed
    '& img': {
      width: '100%',
      height: 'auto',
      position: 'absolute',
  
      display: 'block',
    },
    '&:hover img': {
      animation: 'scrollImage 6s linear infinite', // Adjust timing as needed
    },
    '@keyframes scrollImage': {
      '0%': {
        transform: 'translateY(0)', // Start at bottom
      },
      '100%': {
        transform: 'translateY(calc(-100% + 300px))', // Move up, adjusted for container height
      },
    },
  });

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
          lg: 'repeat(4, 1fr)',
        },
        gap: { xs: 12, md:19 },
        
        // padding: { xs: 1, md: 2 },
      }}
      // mr={12}
    >
      {images.map((item, i) => (
        <Card key={`${item.title}-${i}`}>
          <ImageWrapper>
            <a href={item.previewlink} target="_blank" rel="noopener noreferrer">
              <img
                src={item.image}
                alt={`${item.title} preview`}
                loading="lazy"
              />
            </a>
          </ImageWrapper>
          <ButtonGroup>
            <Button
              fullWidth
              variant="contained"
              size={isMobile ? 'small' : 'medium'}
              sx={{
                backgroundColor: COLORS.primary,
                '&:hover': { backgroundColor: COLORS.hover },
                mb: 1,
              }}
                component="a"
              href={item.previewlink}
              target="_blank"
              rel="noopener noreferrer"
            >
              Preview
            </Button>
            <Button
              fullWidth
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
              sx={{
                borderColor: COLORS.primary,
                color: COLORS.primary,
                '&:hover': {
                  borderColor: COLORS.hover,
                  color: COLORS.hover,
                },
              }}
              onClick={() => handleNavigate(item.title)}
            >
              Use Template
            </Button>
          </ButtonGroup>
        </Card>
      ))}
    </Box>
  );
});

const BigFiveCRMTemplates = () => {
  const [value, setValue] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));
  const primaryColor = "#8C7AA2";

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'center',
          py: 4,
          px:3
        }}
      >
        <Box
          sx={{
            width: isMobile ? '100%' : '10%',
            display: 'flex',
            justifyContent: isMobile ? 'center' : 'flex-start',
            // mb: isMobile ? 2 : 0,
          }}
        >
          <Link>
          <img
            src={logo}
            style={{
              width: isMobile ? '40%' : '80%',
              height: 'auto',
              maxWidth: '100px',
            }}
            alt="Logo"
          />
          </Link>
          
        </Box>
        <Box
          sx={{
            width: isMobile ? '100%' : '110%',
            textAlign: 'center',
          }}
        >
          <Typography
            variant={isMobile ? 'h5' : 'h6'}
            sx={{
              fontWeight: '900',
              color: primaryColorPurple,
              fontFamily: 'Nunito Sans',
              fontSize: isMobile ? '1.5rem' : isTablet ? '2rem' : '2rem',
            }}
          >
            BigFive CRM Templates
          </Typography>
         
                <motion.div variants={textVariants}>
                  <Typography
                    variant={isMobile ? 'h5' : 'h2'}
                    sx={{
                      color: 'gray',
                      fontFamily: 'Nunito Sans',
                      fontSize: isMobile ? '1.5rem' : isTablet ? '2rem' : '2rem',
                    }}
                    mt={2}
                  >
                   Design your own website today
                   for FREE!
                  </Typography>
                </motion.div>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ display: "flex", justifyContent: "center", width: "100%", top: 20 }}>
        <Box
          sx={{
            width: isMobile ? "100%" : isTablet ? "95%" : "100%",
            maxWidth: "1400px",
          }}
        >
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
            
            }}
          >
            <Tabs
              value={value}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              className={responsiveStyle.activeButton}
              sx={{
                "& .MuiTabs-indicator": { backgroundColor: COLORS.primary },
                "& .MuiTab-root": {
                  color: "gray",
                  minWidth: "150px",
                  borderRadius:1,
                  px:  1,
                  // fontSize: isMobile ? "0.8rem" : isTablet ? "0.9rem" : "1rem",
                },
                "& .Mui-selected": {
                  color: "white !important",
                  backgroundColor:  COLORS.primary,
                  fontWeight: "bold",
                },
                "& .MuiTabScrollButton-root": {
                  color:  COLORS.primary
                }
              }}
            >
              {tabs.map((title, index) => (
                <Tab key={index} label={title}  />
              ))}
            </Tabs>
          </Box>

          {/* Tab Content Section */}
          <Box
            sx={{
              backgroundColor: "none",
              padding: isMobile ? 1 : isTablet ? 2 : 3,
              borderRadius: 2,
              height: isMobile ? "100%" : isTablet ? "100%" : "400px",
              mt: 1,
              mb: 2,


            }}
          >
            <TabPanel
              value={value}
              index={value}
              images={groupedData[tabs[value]]}
            />
          </Box>
          <Box sx={{bottom:0}}>
                <Divider
                  sx={{
                    borderColor: 'lightgray',
                    my: 1, // Margin above and below the divider
                  }}
                />

                {/* Footer */}
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="body2" color="gray">
                    {"Privacy Policy | Terms & Conditions | © 2025 Company Name. All rights reserved."}
                  </Typography>
                </Box>
              </Box>
        </Box>
        
      </Box>
    
      
    </Box>
  );
};

export default BigFiveCRMTemplates;